import { useState, type ReactNode } from 'react';
import { FiArrowLeft, FiFileText, FiUsers, FiDollarSign, FiClock, FiShield, FiBox, FiAward, FiCalendar, FiFilePlus, FiArchive, FiTrendingDown, FiPaperclip, FiSend } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from '../../ions/Loading';
import { formatDate } from '../../../utils/collateralFormatters';
import useLegalCaseDetail from '../../../hooks/useLegalCaseDetail';
import LegalCaseParties from './shared/LegalCaseParties';
import LegalCaseClaim from './shared/LegalCaseClaim';
import LegalCaseCaseEvents from './shared/LegalCaseCaseEvents';
import LegalCaseMeasuresDeadlines from './shared/LegalCaseMeasuresDeadlines';
import LegalCaseSeizedAssets from './shared/LegalCaseSeizedAssets';
import LegalCaseCourtDecisions from './shared/LegalCaseCourtDecisions';
import LegalCaseHearings from './shared/LegalCaseHearings';
import LegalCaseAgreements from './shared/LegalCaseAgreements';
import LegalCaseDeposits from './shared/LegalCaseDeposits';
import LegalCasePayments from './shared/LegalCasePayments';
import LegalCaseDocuments from './shared/LegalCaseDocuments';
import ConsultationPasswordField from './shared/ConsultationPasswordField';
import LegalCaseExpenses from './shared/LegalCaseExpenses';
import CaseResultCard from './shared/CaseResultCard';
import Cpc25RiskCard from './shared/Cpc25RiskCard';
import BeneficiaryContact from './shared/BeneficiaryContact';
import LegalCaseCollection from './shared/LegalCaseCollection';
import CaseOriginBadge from './shared/CaseOriginBadge';
import CaseLinksBlock from './shared/CaseLinksBlock';
import { HelpButton } from './shared/SectionHelp';
import { collectionTitles } from './shared/collectionTitles';
import { CaseOrigin, CREDITOR_ENTITY_TO_BE_DEFINED, type LegalCaseDetail } from '../../../types/LegalCase';

// Seções do detalhe. Cada task futura (andamento, medidas, penhora, etc.) adiciona uma
// entrada aqui, sem mexer na navegação — foi o motivo de migrar do painel lateral para a
// página dedicada com navegação por seção (ver PROGRESS.md, Task 4).
type SectionId = 'summary' | 'parties' | 'claim' | 'collection' | 'events' | 'measures' | 'seizedAssets' | 'courtDecisions' | 'hearings' | 'agreements' | 'deposits' | 'payments' | 'documents' | 'expenses';

const SECTIONS: { id: SectionId; label: string; icon: ReactNode }[] = [
    { id: 'summary', label: 'Resumo', icon: <FiFileText size={16} /> },
    { id: 'parties', label: 'Partes', icon: <FiUsers size={16} /> },
    { id: 'claim', label: 'Pretensão', icon: <FiDollarSign size={16} /> },
    // Task 16 (AF-3694): o que a cobrança fez com cada título enviado. Logo depois da Pretensão,
    // que lista os mesmos títulos pelo lado do jurídico.
    { id: 'collection', label: 'Cobrança', icon: <FiSend size={16} /> },
    { id: 'events', label: 'Andamento', icon: <FiClock size={16} /> },
    { id: 'measures', label: 'Medidas e prazos', icon: <FiShield size={16} /> },
    { id: 'seizedAssets', label: 'Penhora', icon: <FiBox size={16} /> },
    { id: 'courtDecisions', label: 'Decisões', icon: <FiAward size={16} /> },
    { id: 'hearings', label: 'Audiências', icon: <FiCalendar size={16} /> },
    { id: 'agreements', label: 'Acordos', icon: <FiFilePlus size={16} /> },
    { id: 'deposits', label: 'Depósitos', icon: <FiArchive size={16} /> },
    { id: 'payments', label: 'Pagamentos', icon: <FiDollarSign size={16} /> },
    { id: 'documents', label: 'Documentos', icon: <FiPaperclip size={16} /> },
    { id: 'expenses', label: 'Custos', icon: <FiTrendingDown size={16} /> },
];

// A seção Cobrança só aparece quando o caso tem título da cobrança: num caso manual ela abriria
// vazia. Fora do componente (diretriz: sem função dentro de função).
const visibleSections = (detail: LegalCaseDetail) =>
    collectionTitles(detail.claim).length > 0
        ? SECTIONS
        : SECTIONS.filter((s) => s.id !== 'collection');

// Caso aberto pela cobrança (Task 16 / AF-3694): diz de onde ele veio e o que falta preencher. A
// entidade credora nasce "A definir" porque o evento da cobrança não a informa.
const CollectionOriginNotice = ({ detail }: { detail: LegalCaseDetail }) => {
    if (detail.origin !== CaseOrigin.COLLECTION) return null;
    const entityPending = detail.creditorEntity === CREDITOR_ENTITY_TO_BE_DEFINED;
    return (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
            <p className="text-sm text-blue-800 dark:text-blue-200">
                Caso aberto pela cobrança, quando ela enviou o título para o jurídico.
                {entityPending && ' Troque a entidade do grupo, que veio "A definir", e confira as taxas de juros e multa em Editar.'}
            </p>
            <HelpButton topic="case.collection" label="Abrir ajuda: caso que veio da cobrança" />
        </div>
    );
};

const SummarySection = ({ detail, onEdit }: { detail: LegalCaseDetail; onEdit: () => void }) => (
    <div className="space-y-4">
        <div className="flex items-start justify-between gap-2">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{detail.creditorEntity}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {detail.caseTypeFormatted} • {detail.poleFormatted} • {detail.statusFormatted}
                </p>
            </div>
            <button
                onClick={onEdit}
                className="flex-shrink-0 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
            >
                Editar
            </button>
        </div>

        <CollectionOriginNotice detail={detail} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500 dark:text-gray-400">Nº do processo:</span> <span className="dark:text-white font-mono text-xs">{detail.externalCaseNumber || '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">UF:</span> <span className="dark:text-white">{detail.state || '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Foro:</span> <span className="dark:text-white">{detail.court || '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Comarca:</span> <span className="dark:text-white">{detail.district || '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Vara:</span> <span className="dark:text-white">{detail.courtDivision || '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Distribuição:</span> <span className="dark:text-white">{detail.openedAt ? formatDate(detail.openedAt) : '—'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Valor da causa:</span> <span className="dark:text-white">{detail.claimValueFormatted || '—'}</span></div>
        </div>

        <CaseLinksBlock links={detail.caseLinks} />

        {/* Encerramento (Task 20): mostra por quê e quando o caso terminou, quando encerrado. */}
        {detail.status === 'CLOSED' && (
            <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Encerramento
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                        <span className="text-gray-500 dark:text-gray-400">Motivo:</span>{' '}
                        <span className="dark:text-white">{detail.closureReasonFormatted || '—'}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 dark:text-gray-400">Data:</span>{' '}
                        <span className="dark:text-white">{detail.closedAt ? formatDate(detail.closedAt) : '—'}</span>
                    </div>
                </div>
                {detail.closureNotes && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">{detail.closureNotes}</p>
                )}
            </div>
        )}

        <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Escritório responsável
            </h4>
            {detail.responsibleFirm ? (
                <div className="space-y-1.5">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {detail.responsibleFirm.name}
                    </p>
                    <BeneficiaryContact beneficiary={detail.responsibleFirm} />
                </div>
            ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhum escritório definido. Use Editar para vincular.
                </p>
            )}
        </div>

        <CaseResultCard claim={detail.claim} expensesSummary={detail.expensesSummary} />

        <Cpc25RiskCard risk={detail.risk} claimValueFormatted={detail.claimValueFormatted} />

        <ConsultationPasswordField password={detail.consultationPassword} />

        {detail.courtLink && (
            <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                <a href={detail.courtLink} target="_blank" rel="noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Abrir no portal do tribunal
                </a>
            </div>
        )}

        {detail.description && (
            <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Observações</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{detail.description}</p>
            </div>
        )}
    </div>
);

// Página de detalhe dedicada de um caso jurídico, com navegação por seção. Substitui o
// painel lateral da listagem (padrão que escala para todas as seções das próximas tasks).
const LegalCasePageDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { detail, isLoading, notFound, refresh } = useLegalCaseDetail(id);
    const [section, setSection] = useState<SectionId>('summary');

    // O Resumo (card de resultado, escritório, valores) lê do page-view carregado uma vez.
    // As seções (Custos, Depósitos, Pretensão) mutam dados no próprio estado, sem tocar o
    // page-view — então, ao voltar para o Resumo, re-busca o detalhe para refletir o que
    // mudou nas outras abas (senão o usuário precisaria sair e reabrir o caso).
    const handleSelectSection = (next: SectionId) => {
        if (next === 'summary' && section !== 'summary') refresh();
        setSection(next);
    };

    if (isLoading) return <Loading />;

    if (notFound || !detail) {
        return (
            <div className="p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">Caso não encontrado.</p>
                <button onClick={() => navigate('/recovery/legal-cases')} className="mt-3 text-blue-600 dark:text-blue-400 hover:underline">
                    Voltar para a lista
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-800">
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 flex items-center gap-3">
                <button
                    onClick={() => navigate('/recovery/legal-cases')}
                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                    title="Voltar"
                >
                    <FiArrowLeft size={18} />
                </button>
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {detail.externalCaseNumber || detail.creditorEntity}
                        </h1>
                        <CaseOriginBadge origin={detail.origin} originFormatted={detail.originFormatted} />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{detail.caseTypeFormatted}</p>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                <nav className="flex-shrink-0 w-52 border-r border-gray-200 dark:border-slate-700 p-3 space-y-1 overflow-auto">
                    {visibleSections(detail).map((s) => (
                        <button
                            key={s.id}
                            onClick={() => handleSelectSection(s.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${section === s.id
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                        >
                            {s.icon}
                            <span>{s.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="flex-1 overflow-auto p-4">
                    {section === 'summary' && (
                        <SummarySection detail={detail} onEdit={() => navigate(`/recovery/legal-cases/${detail.id}/edit`)} />
                    )}
                    {section === 'parties' && (
                        <LegalCaseParties caseId={detail.id} initialParties={detail.parties} />
                    )}
                    {section === 'claim' && (
                        <LegalCaseClaim caseId={detail.id} initialClaim={detail.claim} />
                    )}
                    {section === 'collection' && (
                        <LegalCaseCollection claim={detail.claim} />
                    )}
                    {section === 'events' && (
                        <LegalCaseCaseEvents caseId={detail.id} initialEvents={detail.events} />
                    )}
                    {section === 'measures' && (
                        <LegalCaseMeasuresDeadlines
                            caseId={detail.id}
                            initialMeasures={detail.measures}
                            initialDeadlines={detail.deadlines}
                        />
                    )}
                    {section === 'seizedAssets' && (
                        <LegalCaseSeizedAssets caseId={detail.id} initialAssets={detail.seizedAssets} />
                    )}
                    {section === 'courtDecisions' && (
                        <LegalCaseCourtDecisions caseId={detail.id} initialDecisions={detail.courtDecisions} />
                    )}
                    {section === 'hearings' && (
                        <LegalCaseHearings caseId={detail.id} initialHearings={detail.hearings} />
                    )}
                    {section === 'agreements' && (
                        <LegalCaseAgreements caseId={detail.id} initialAgreements={detail.agreements} />
                    )}
                    {section === 'deposits' && (
                        <LegalCaseDeposits
                            caseId={detail.id}
                            initialDeposits={detail.deposits}
                            initialSummary={detail.depositsSummary}
                            claim={detail.claim}
                            pole={detail.pole}
                        />
                    )}
                    {section === 'payments' && (
                        <LegalCasePayments caseId={detail.id} claim={detail.claim} />
                    )}
                    {section === 'documents' && (
                        <LegalCaseDocuments caseId={detail.id} initialDocuments={detail.documents} />
                    )}
                    {section === 'expenses' && (
                        <LegalCaseExpenses
                            caseId={detail.id}
                            initialExpenses={detail.expenses}
                            initialSummary={detail.expensesSummary}
                            defaultBeneficiaryId={detail.responsibleFirmId}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LegalCasePageDetail;
